
import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

type RoleProtectedRouteProps = {
  children: ReactNode;
  requiredRole: 'admin' | 'user';
};

const RoleProtectedRoute = ({ children, requiredRole }: RoleProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const [hasRole, setHasRole] = useState<boolean | null>(null);
  const [isCheckingRole, setIsCheckingRole] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkUserRole = async () => {
      if (!user) {
        setIsCheckingRole(false);
        return;
      }

      try {
        // Check if user has the required role using the database function
        const { data, error } = await supabase.rpc('has_role', {
          _user_id: user.id,
          _role: requiredRole
        });

        if (error) {
          console.error('Error checking user role:', error);
          toast.error('Error verifying permissions');
          setHasRole(false);
        } else {
          setHasRole(data);
        }
      } catch (error) {
        console.error('Error checking role:', error);
        setHasRole(false);
      } finally {
        setIsCheckingRole(false);
      }
    };

    if (!loading) {
      checkUserRole();
    }
  }, [user, loading, requiredRole, navigate]);

  // Redirect if the user doesn't have the required role
  useEffect(() => {
    if (!loading && !isCheckingRole && !hasRole) {
      toast.error("You don't have permission to access this page");
      navigate('/dashboard');
    }
  }, [hasRole, isCheckingRole, loading, navigate]);

  if (loading || isCheckingRole) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Checking permissions...</span>
      </div>
    );
  }

  // Only render children if the user has the required role
  return hasRole ? <>{children}</> : null;
};

export default RoleProtectedRoute;
